/**
 * Browser-side legacy agentic flow JSON → FlowDefinitionV2 (mirrors `server/utils/legacyToV2Converters.js`).
 * Keep in sync when server converter logic changes.
 */

const EXECUTOR_NATIVE_TYPES = new Set([
  'start', 'end', 'step', 'llm', 'api', 'code', 'conditional', 'human-input', 'parallel', 'join',
  'ai-agent', 'api-call', 'code-executor', 'rag', 'policy', 'form', 'database', 'email', 'sms',
  'calendar', 'webhook', 'loop', 'transform', 'delay', 'scheduler', 'decision-tree', 'switch',
  'try-catch', 'router', 'filter', 'sort', 'merge', 'group', 'aggregate', 'chat', 'message-queue',
  'voice-call', 'social-post', 'trigger', 'mcp-tool', 'tool', 'memory', 'debugger', 'logger',
  'notification', 'execute-flow', 'connector', 'web-crawler', 'db_query', 'db_insert', 'db_update',
  'db_delete', 'http_request', 'file-operations', 'data-validation', 'memory-store',
]);

const NODE_TYPE_WARNING = /^Node ".*" has (?:legacy )?type "([^"]+)"/;

/** Drops saved node-type warnings for types the executor now supports (older migrations flagged them). */
export function filterStaleImportWarnings(warnings) {
  return (warnings || []).filter((line) => {
    const match = NODE_TYPE_WARNING.exec(String(line));
    return !(match && EXECUTOR_NATIVE_TYPES.has(match[1]));
  });
}

export function legacyFlowToV2Client(legacy) {
  const warnings = [];
  const rawNodes = legacy.nodes || [];
  const ids = new Set();
  rawNodes.forEach((n) => {
    const id = n?.id != null ? String(n.id) : '';
    if (!id) warnings.push('Skipped a legacy node with no id.');
    else if (ids.has(id)) warnings.push(`Duplicate legacy node id "${id}" — last wins in export.`);
    else ids.add(id);
    const t = n?.type != null ? String(n.type) : '';
    if (t && !EXECUTOR_NATIVE_TYPES.has(t)) {
      warnings.push(`Node "${id || '(no id)'}" has type "${t}" — verify Python executor support.`);
    }
  });
  const nodeIdSet = new Set(rawNodes.map((n) => (n?.id != null ? String(n.id) : '')).filter(Boolean));
  (legacy.edges || []).forEach((e) => {
    const s = e?.source != null ? String(e.source) : '';
    const t = e?.target != null ? String(e.target) : '';
    if (s && !nodeIdSet.has(s)) warnings.push(`Edge references missing source node "${s}".`);
    if (t && !nodeIdSet.has(t)) warnings.push(`Edge references missing target node "${t}".`);
  });

  return {
    schemaVersion: 'flow/v2',
    id: String(legacy._id || legacy.id || `flow_${Date.now()}`),
    name: legacy.name || 'Migrated Flow',
    description: legacy.description || '',
    status: legacy.status || 'draft',
    nodes: (legacy.nodes || []).map((n) => ({
      id: n.id,
      type: n.type,
      label: n.label || n.data?.label || n.type,
      position: n.position || { x: 0, y: 0 },
      config: n.data?.config || n.config || {},
    })),
    edges: (legacy.edges || []).map((e) => ({
      id: e.id || `e_${e.source}_${e.target}`,
      source: e.source,
      target: e.target,
      kind: e.kind || 'control',
    })),
    metadata: {
      source: 'legacy-import-client',
      convertedAt: new Date().toISOString(),
      warnings: warnings.length ? warnings : undefined,
    },
  };
}
